import opportunityInfoService from '../services/oneButtonServices/opportunityInfoService';
import type { LeadSyncPayload } from '../services/oneButtonServices/quoteServices';

function financePartnerLeadList(od: any): any[] {
    if (!od) return [];
    const nested = od.OpportunityDetails ?? od.opportunityDetails;
    const inner = od.data ?? od.Data;
    const list =
        od.FinancePartnerLeadList ??
        od.financePartnerLeadListModels ??
        od.financePartnerLeadList ??
        nested?.FinancePartnerLeadList ??
        nested?.financePartnerLeadListModels ??
        nested?.financePartnerLeadList ??
        inner?.FinancePartnerLeadList ??
        inner?.financePartnerLeadListModels ??
        inner?.financePartnerLeadList ??
        od.opportunityInformation?.financePartnerLeadListModels ??
        [];
    return Array.isArray(list) ? list : [];
}

function rowProductType(ele: any): string {
    return String(
        ele?.ProductTypeName ??
            ele?.productTypeName ??
            ele?.Product_Type_Name ??
            ele?.lead_Type ??
            ele?.Lead_Type ??
            '',
    ).toLowerCase();
}

function rowPartner(ele: any): string {
    return String(
        ele?.FinancePartnerName ??
            ele?.financePartnerName ??
            ele?.Finance_Partner ??
            ele?.Finance_Partner_Name ??
            ele?.finance_Partner ??
            ele?.Finance_Partner_Display_Name ??
            '',
    ).toLowerCase();
}

function rowMethod(ele: any): string {
    return String(
        ele?.FinanceMethodName ??
            ele?.financeMethodName ??
            ele?.Finance_Method_Name ??
            ele?.finance_Method_Name ??
            ele?.Finance_Method ??
            '',
    );
}

function rowLeadId(ele: any): string | null {
    const id =
        ele?.Finance_Partner_Sync_LeadID ??
        ele?.FinancePartnerSyncLeadId ??
        ele?.financePartnerSyncLeadId ??
        ele?.finance_Partner_Sync_LeadID ??
        ele?.LeadId ??
        ele?.leadId ??
        ele?.lead_Id;
    return id != null && String(id).trim() !== '' ? String(id) : null;
}

function opportunityProductRows(od: any): any[] {
    if (!od) return [];
    const nested = od.OpportunityDetails ?? od.opportunityDetails;
    const list =
        od.OpportunityProduct ??
        od.opportunityProduct ??
        nested?.OpportunityProduct ??
        nested?.opportunityProduct ??
        [];
    return Array.isArray(list) ? list : [];
}

/** IGS sync lead ids (e.g. 26RI-QXYMD). GetPricingLRBO / Lightreach GetPriceCaps 400 or return empty on these. */
function isIgsLeadId(id: string | null | undefined): boolean {
    return /^26RI-/i.test(String(id ?? '').trim());
}

function unwrapRetrievedOpportunity(retrieved: any): any {
    if (!retrieved || typeof retrieved !== 'object') return retrieved;
    if (
        retrieved.financePartnerLeadListModels ||
        retrieved.FinancePartnerLeadList ||
        retrieved.financePartnerLeadList ||
        retrieved.OpportunityDetails ||
        retrieved.opportunityDetails
    ) {
        return retrieved;
    }
    const inner = retrieved.data ?? retrieved.Data;
    if (inner && typeof inner === 'object' && inner !== retrieved) {
        return unwrapRetrievedOpportunity(inner);
    }
    return retrieved;
}

function leadListFromRetrieve(retrieved: any): any[] {
    const root = unwrapRetrievedOpportunity(retrieved);
    if (!root) return [];
    return financePartnerLeadList(root);
}

function normalizeLeadListProductKey(value: unknown): string {
    return String(value ?? '')
        .trim()
        .toLowerCase()
        .replace(/\s+only$/, '');
}

function leadListRowKey(row: any): string {
    const partner = rowPartner(row).replace(/\s+/g, '');
    const product = normalizeLeadListProductKey(
        row?.ProductTypeName ?? row?.productTypeName ?? row?.Product_Type_Name ?? row?.lead_Type,
    );
    return `${partner}|${product}`;
}

function leadRowTimestamp(row: any): number {
    const raw =
        row?.LastModifiedDate ??
        row?.lastModifiedDate ??
        row?.CreatedDate ??
        row?.createdDate ??
        row?.ModifiedDate ??
        '';
    const parsed = Date.parse(String(raw));
    return Number.isFinite(parsed) ? parsed : 0;
}

/** Keep every product/partner lead when RetrieveOpportunityDetails returns a partial list after one sync. */
export function mergeFinancePartnerLeadLists(existing: any[], incoming: any[]): any[] {
    const prev = Array.isArray(existing) ? existing : [];
    const next = Array.isArray(incoming) ? incoming : [];
    if (prev.length === 0) return [...next];
    if (next.length === 0) return [...prev];

    const byKey = new Map<string, any>();
    for (const row of prev) {
        const key = leadListRowKey(row);
        if (key !== '|') byKey.set(key, row);
    }
    for (const row of next) {
        const key = leadListRowKey(row);
        if (key === '|') continue;
        const prior = byKey.get(key);
        const incomingId = rowLeadId(row);
        const priorId = prior ? rowLeadId(prior) : null;
        if (!prior) {
            byKey.set(key, row);
            continue;
        }
        const incomingTs = leadRowTimestamp(row);
        const priorTs = leadRowTimestamp(prior);
        if (incomingId && (!priorId || incomingTs >= priorTs)) {
            byKey.set(key, { ...prior, ...row, Finance_Partner_Sync_LeadID: incomingId });
        } else if (priorId && !incomingId) {
            byKey.set(key, { ...row, ...prior, Finance_Partner_Sync_LeadID: priorId });
        } else {
            byKey.set(key, { ...prior, ...row });
        }
    }
    return Array.from(byKey.values());
}

function leadListProductTypesMatch(leadProductType: string, opportunityProductType: string): boolean {
    const leadKey = normalizeLeadListProductKey(leadProductType);
    const oppKey = normalizeLeadListProductKey(opportunityProductType);
    if (leadKey === oppKey) return true;
    if (oppKey.includes('battery') && leadKey.includes('battery')) return true;
    if (oppKey.includes('roof') && leadKey.includes('roof')) return true;
    return false;
}

function findLeadRowForOpportunityProduct(list: any[], product: any): any | undefined {
    const productType = String(product?.Product_Type_Name ?? product?.product_Type_Name ?? '').trim();
    if (!productType) return undefined;
    const partnerKey = rowPartner({
        FinancePartnerName: product?.Finance_Partner_Display_Name ?? product?.Finance_Partner,
    }).replace(/\s+/g, '');

    return list.find((ele) => {
        const leadProductType = String(
            ele?.ProductTypeName ?? ele?.productTypeName ?? ele?.Product_Type_Name ?? '',
        ).trim();
        if (!leadListProductTypesMatch(leadProductType, productType)) return false;
        const leadPartnerKey = rowPartner(ele).replace(/\s+/g, '');
        if (partnerKey && leadPartnerKey && !leadPartnerKey.includes(partnerKey) && !partnerKey.includes(leadPartnerKey)) {
            return false;
        }
        return rowLeadId(ele) != null;
    });
}

/** V1 retrieveLeadDetailsById: copy FinancePartnerLeadList ids onto OpportunityProduct rows. */
export function applyLeadListToOpportunityProducts(od: any): any {
    if (!od) return od;
    const list = financePartnerLeadList(od);
    if (!list.length) return od;

    const patchProducts = (rows: any[]) =>
        rows.map((product) => {
            const matched = findLeadRowForOpportunityProduct(list, product);
            const id = matched ? rowLeadId(matched) : null;
            if (!id) return product;
            return {
                ...product,
                Finance_Partner_Sync_LeadID: id,
                finance_Partner_Sync_LeadID: id,
            };
        });

    const opp = od.OpportunityProduct ?? od.opportunityProduct;
    const nested = od.OpportunityDetails ?? od.opportunityDetails;
    const nestedOpp = nested?.OpportunityProduct ?? nested?.opportunityProduct;

    let next = od;
    if (Array.isArray(opp)) {
        const patched = patchProducts(opp);
        next = { ...next, OpportunityProduct: patched, opportunityProduct: patched };
    }
    if (nested && Array.isArray(nestedOpp)) {
        const patchedNested = patchProducts(nestedOpp);
        const patchedNestedBlock = { ...nested, OpportunityProduct: patchedNested, opportunityProduct: patchedNested };
        next = {
            ...next,
            OpportunityDetails: patchedNestedBlock,
            opportunityDetails: patchedNestedBlock,
        };
    }
    return next;
}

/** Attach V1 RetrieveOpportunityDetails `financePartnerLeadListModels` onto the V2 opportunity snapshot. */
export function withFinancePartnerLeadList(od: any, retrieved: any): any {
    if (!od) return od;
    const incoming = leadListFromRetrieve(retrieved);
    const existing = financePartnerLeadList(od);
    const list = mergeFinancePartnerLeadLists(existing, incoming);
    if (list.length === 0) return od;
    return applyLeadListToOpportunityProducts({
        ...od,
        financePartnerLeadListModels: list,
        FinancePartnerLeadList: list,
    });
}

export type SyncedFinancePartnerLeadRow = {
    partner: string;
    productType: string;
    leadId: string;
};

function productCodeFromLeadProductType(type: string | null | undefined): string {
    const raw = String(type || '').trim().toLowerCase();
    const t = normalizeLeadListProductKey(raw);
    if (t === 'battery') return 'B';
    // V1 reSyncProductType: Roof Only is RO. Category R is rejected by ResetSyncData ("Sync not happened").
    if (t === 'roof') return raw === 'roof only' || raw === 'ro' ? 'RO' : 'R';
    if (t === 'rrr') return 'RRR';
    if (t === 'rr') return 'RR';
    if (t === 'solar') return 'S';
    return 'S';
}

/**
 * V1 sunlight-lead-model: ResetSyncData body must include other products' leads
 * (e.g. keep Solar when syncing Battery) or the API drops them.
 */
export function buildKeepSyncedLeadsResetPayload(opportunityDetails: any): LeadSyncPayload[] {
    const seen = new Set<string>();
    const out: LeadSyncPayload[] = [];

    const pushRow = (leadId: string, productType: string) => {
        const type = String(productType || '').trim();
        if (!leadId || !type) return;
        const category = productCodeFromLeadProductType(type);
        const key = `${leadId}|${category}`;
        if (seen.has(key)) return;
        seen.add(key);
        const normalizedType = normalizeLeadListProductKey(type) === 'battery' ? 'Battery' : type;
        out.push({
            finance_Partner_Lead_Sync_Id: leadId,
            productType: normalizedType,
            address: false,
            title: false,
            product_Category: category,
        });
    };

    for (const row of financePartnerLeadList(opportunityDetails)) {
        const id = rowLeadId(row);
        if (!id) continue;
        const productType = String(
            row?.ProductTypeName ?? row?.productTypeName ?? row?.Product_Type_Name ?? row?.lead_Type ?? '',
        ).trim();
        pushRow(id, productType);
    }

    for (const product of opportunityProductRows(opportunityDetails)) {
        const id = rowLeadId(product);
        if (!id) continue;
        const productType = String(product?.Product_Type_Name ?? product?.product_Type_Name ?? '').trim();
        pushRow(id, productType);
    }

    for (const row of listSyncedIgsAndLightreachLeads(opportunityDetails)) {
        pushRow(String(row.leadId || '').trim(), row.productType);
    }

    return out;
}

export function keepSyncedLeadsExceptProductCodes(
    opportunityDetails: any,
    excludeProductCodes: Set<string>,
): LeadSyncPayload[] {
    return buildKeepSyncedLeadsResetPayload(opportunityDetails).filter(
        (row) => !excludeProductCodes.has(row.product_Category),
    );
}

/** Merge other synced products into ResetSyncData payload (V1 reSyncLead parity). */
export function mergeResetSyncPayloadPreservingOtherProducts(
    opportunityDetails: any,
    syncPayload: LeadSyncPayload[] | undefined,
): LeadSyncPayload[] {
    const incoming = Array.isArray(syncPayload) ? syncPayload : [];
    const syncingCategories = new Set(
        incoming.map((row) => String(row.product_Category || productCodeFromLeadProductType(row.productType)).toUpperCase()),
    );
    const keepOthers = buildKeepSyncedLeadsResetPayload(opportunityDetails).filter(
        (row) => !syncingCategories.has(String(row.product_Category).toUpperCase()),
    );
    const byCategory = new Map<string, LeadSyncPayload>();
    // V1 reSyncLead: preserved product rows use address/title false so utility/lead-system stay synced.
    for (const row of keepOthers) {
        const cat = String(row.product_Category || productCodeFromLeadProductType(row.productType)).toUpperCase();
        if (!cat) continue;
        byCategory.set(cat, {
            ...row,
            address: row.address === true,
            title: row.title === true,
        });
    }
    for (const row of incoming) {
        const cat = String(row.product_Category || productCodeFromLeadProductType(row.productType)).toUpperCase();
        if (cat) byCategory.set(cat, row);
    }
    return Array.from(byCategory.values());
}

/**
 * True when every payload row re-links a lead id already bound on this opportunity (Sync with Lead ID refresh).
 * Skip ResetSyncData in that case so utility / lead-system sync flags are not cleared.
 */
export function isExistingLeadResyncPayload(
    opportunityDetails: any,
    payload: LeadSyncPayload[] | undefined,
): boolean {
    if (!Array.isArray(payload) || payload.length === 0) return false;

    const boundIds = new Set<string>();
    for (const row of financePartnerLeadList(opportunityDetails)) {
        const id = rowLeadId(row);
        if (id) boundIds.add(id);
    }
    for (const product of opportunityProductRows(opportunityDetails)) {
        const id = rowLeadId(product);
        if (id) boundIds.add(id);
    }

    return payload.every((row) => {
        const syncId = String(row.finance_Partner_Lead_Sync_Id ?? '').trim();
        return !!syncId && boundIds.has(syncId);
    });
}

function displayPartnerFromLeadRow(ele: any, leadId: string): string {
    const partner = rowPartner(ele);
    if (partner.includes('lightreach')) return 'Lightreach';
    if (partner.includes('igs') || isIgsLeadId(leadId)) return 'IGS';
    const raw = String(
        ele?.FinancePartnerName ?? ele?.financePartnerName ?? ele?.Finance_Partner_Name ?? '',
    ).trim();
    return raw || 'Lead';
}

function displayProductTypeFromLeadRow(ele: any): string {
    const raw = String(
        ele?.ProductTypeName ??
            ele?.productTypeName ??
            ele?.Product_Type_Name ??
            ele?.lead_Type ??
            ele?.Lead_Type ??
            '',
    ).trim();
    if (!raw) return '';
    if (/^battery$/i.test(raw)) return 'Battery Only';
    return raw;
}

/**
 * Every synced IGS / Lightreach lead on the opportunity (V1 combined lead list).
 * Used by Finance Partner Sync to list what is already synced — not only the IGS 26RI- id.
 */
export function listSyncedIgsAndLightreachLeads(od: any): SyncedFinancePartnerLeadRow[] {
    const list = financePartnerLeadList(od);
    const rows: SyncedFinancePartnerLeadRow[] = [];
    const seen = new Set<string>();
    const productOrder = ['solar', 'roof', 'battery', 'rrr', 'rr'];
    for (const ele of list) {
        const leadId = rowLeadId(ele);
        if (!leadId) continue;
        let partner = displayPartnerFromLeadRow(ele, leadId);
        if (partner !== 'IGS' && partner !== 'Lightreach') {
            const pt = displayProductTypeFromLeadRow(ele).toLowerCase();
            if (isIgsLeadId(leadId)) partner = 'IGS';
            else if (pt.includes('battery')) partner = 'Lightreach';
            else continue;
        }
        const productType = displayProductTypeFromLeadRow(ele);
        const key = `${partner}|${productType}|${leadId}`.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        rows.push({ partner, productType, leadId });
    }
    rows.sort((a, b) => {
        if (a.partner !== b.partner) return a.partner === 'IGS' ? -1 : 1;
        const ai = productOrder.findIndex((p) => a.productType.toLowerCase().includes(p));
        const bi = productOrder.findIndex((p) => b.productType.toLowerCase().includes(p));
        return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    });
    return rows;
}

/** Lightreach Battery Lease (Battery Only) lead — V1 GetPriceCaps / GetPricingLRBO for LR BO. */
export function getLightreachBatteryLeaseLeadIdFromOpportunity(od: any): string | null {
    const list = financePartnerLeadList(od);

    // V1 getPriceCapForLightreach: battery only + lightreach + Battery Lease
    const priceCapRow = list.find((ele: any) => {
        const productType = rowProductType(ele);
        const partner = rowPartner(ele);
        const method = rowMethod(ele);
        return (
            partner.includes('lightreach') &&
            method.includes('Battery Lease') &&
            (productType === 'battery only' || productType.includes('battery'))
        );
    });
    const priceCapId = rowLeadId(priceCapRow);
    if (priceCapId && !isIgsLeadId(priceCapId)) return priceCapId;

    // V1 getEligibleProductForBatteryLightreach: first Battery Lease lead
    const batteryLeaseRow = list.find((ele: any) => {
        if (!rowMethod(ele).includes('Battery Lease')) return false;
        const partner = rowPartner(ele);
        return !partner || partner.includes('lightreach');
    });
    const batteryLeaseId = rowLeadId(batteryLeaseRow);
    if (batteryLeaseId && !isIgsLeadId(batteryLeaseId)) return batteryLeaseId;

    const lightreachBatteryRow = list.find((ele: any) => {
        return rowPartner(ele).includes('lightreach') && rowProductType(ele).includes('battery');
    });
    const lightreachBatteryId = rowLeadId(lightreachBatteryRow);
    if (lightreachBatteryId && !isIgsLeadId(lightreachBatteryId)) return lightreachBatteryId;

    const productRow = opportunityProductRows(od).find((p: any) => {
        const pt = String(p.Product_Type_Name ?? p.product_Type_Name ?? '').toLowerCase();
        const fp = String(
            p.Finance_Partner_Display_Name ?? p.Finance_Partner ?? p.finance_Partner ?? '',
        ).toLowerCase();
        const method = String(p.Finance_Method_Name ?? p.Finance_Method ?? '');
        return pt.includes('battery') && fp.includes('lightreach') && method.includes('Battery Lease');
    });
    const productId = rowLeadId(productRow);
    if (productId && !isIgsLeadId(productId)) return productId;

    return null;
}

/** Resolve LightReach finance-partner sync lead id from opportunity details. */
export function getLightreachLeadIdFromOpportunity(od: any): string | null {
    if (!od) return null;
    const batteryLeaseId = getLightreachBatteryLeaseLeadIdFromOpportunity(od);
    if (batteryLeaseId) return batteryLeaseId;

    const list = financePartnerLeadList(od);
    if (Array.isArray(list)) {
        for (const row of list) {
            const fp = rowPartner(row);
            if (!fp.includes('lightreach')) continue;
            const pt = rowProductType(row);
            if (pt.includes('solar')) {
                const id = rowLeadId(row);
                if (id && !isIgsLeadId(id)) return id;
            }
        }
        for (const row of list) {
            const fp = rowPartner(row);
            if (fp.includes('lightreach')) {
                const id = rowLeadId(row);
                if (id && !isIgsLeadId(id)) return id;
            }
        }
    }

    const hasIgsProduct = opportunityProductRows(od).some((p: any) => {
        const fp = String(
            p.Finance_Partner_Display_Name ?? p.Finance_Partner ?? p.finance_Partner ?? '',
        ).toLowerCase();
        return fp.includes('igs');
    });
    if (hasIgsProduct) return null;

    const top =
        od.Finance_Partner_Sync_LeadID ??
        od.OpportunityDetails?.Finance_Partner_Sync_LeadID ??
        od.opportunityInformation?.OpportunityDetails?.Finance_Partner_Sync_LeadID;
    if (top != null && String(top).trim() !== '' && !isIgsLeadId(String(top))) return String(top);
    return null;
}

const retrieveOpportunityById = new Map<string, Promise<any>>();

/** Drop cached RetrieveOpportunityDetails after lead sync so GetPriceCaps sees the new LR-BO id. */
export function invalidateRetrievedOpportunityDetails(opportunityId?: string | number | null): void {
    if (opportunityId == null || String(opportunityId).trim() === '') {
        retrieveOpportunityById.clear();
        return;
    }
    retrieveOpportunityById.delete(String(opportunityId));
}

/** One RetrieveOpportunityDetails per opportunity id. Does not write the zustand store. */
async function retrievedOpportunityDetails(opportunityId: string): Promise<any | null> {
    let pending = retrieveOpportunityById.get(opportunityId);
    if (!pending) {
        pending = opportunityInfoService.retrieveOpportunityDetails(opportunityId).catch(() => null);
        retrieveOpportunityById.set(opportunityId, pending);
    }
    return pending;
}

/** V1 GetPriceCaps / GetPricingLRBO lead: Battery Lease row, never the IGS 26RI- id. */
export async function resolveLightreachBatteryLeaseLeadId(
    od: any,
    opportunityId: string | number | null | undefined,
): Promise<string | null> {
    const fromSnapshot = getLightreachBatteryLeaseLeadIdFromOpportunity(od);
    if (fromSnapshot) return fromSnapshot;
    if (opportunityId == null || String(opportunityId).trim() === '') return null;
    invalidateRetrievedOpportunityDetails(opportunityId);
    const retrieved = await retrievedOpportunityDetails(String(opportunityId));
    if (!retrieved) return null;
    const merged = withFinancePartnerLeadList(od, retrieved);
    const fromRetrieve = getLightreachBatteryLeaseLeadIdFromOpportunity(merged);
    if (fromRetrieve) return fromRetrieve;
    const retrieveRoot = unwrapRetrievedOpportunity(retrieved);
    return getLightreachBatteryLeaseLeadIdFromOpportunity(retrieveRoot);
}
